import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import axiosInstance from "../utils/axios.config";
import { message, Spin, Modal, Button } from "antd";
import AddOpponentModal from "../components/opponents/AddOpponentModal";
import EditOpponentModal from "../components/opponents/EditOpponentModal";
import ViewOpponentModal from "../components/opponents/ViewOpponentModal";
import Swal from "sweetalert2";
import apiService from "../services/api.service";
import logger from "../utils/logger";

const OpponentsPage = () => {
  // State initialization
  const [opponents, setOpponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({
    total_opponents: 0,
    plaintiffs: 0,  // مدعون
    defendants: 0,  // مدعى عليهم
    with_lawyers: 0,
    with_phones: 0,
    with_emails: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("الكل");
  const [sortBy, setSortBy] = useState("");
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState("all");
  const [activeTab, setActiveTab] = useState("all");
  const [showInactive, setShowInactive] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchOpponents();
    fetchCases();
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === "all") {
      setSelectedCaseId("all");
    }
  }, [activeTab]);

  useEffect(() => {
    fetchOpponents();
    fetchStats();
  }, [showInactive]);

  // Fetch opponents from API
  const fetchOpponents = async () => {
    try {
      setLoading(true);
      setError(null);

      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) {
        logger.error("No token found");
        setError("جلسة المستخدم منتهية. يرجى تسجيل الدخول مرة أخرى.");
        setLoading(false);
        return;
      }
      const response = await axiosInstance.get(
        `/opponents?include_inactive=${showInactive}`
      );

      if (response.data && Array.isArray(response.data.data)) {
        // تحويل البيانات وإضافة خصائص إضافية مفيدة
        const formattedOpponents = [];

        // استخدام حلقة for...of لمعالجة كل خصم على حدة
        for (const opponent of response.data.data) {
            // جلب معلومات القضية المرتبطة بالخصم
            let caseInfo = null;
            if (opponent.id) {
              try {
                // البحث عن القضايا المرتبطة بالخصم في جدول القضايا
                const caseResponse = await axiosInstance.get(
                  `/cases?opponent_id=${opponent.id}`
                );
                if (caseResponse.data && caseResponse.data.data && caseResponse.data.data.length > 0) {
                  // البحث عن القضية التي تطابق معرف الخصم الحالي
                  const matchingCase = caseResponse.data.data.find(c => c.opponent_id == opponent.id);
                  if (matchingCase) {
                    caseInfo = matchingCase;
                  } else {
                    // لا تقم بتعيين قضية إذا لم يتم العثور على مطابقة
                    caseInfo = null;
                  }
                }
              } catch (caseError) {
                logger.error("Error fetching case info:", caseError);
              }
            }

          // إضافة الخصوم مع معلومات القضية إلى القائمة
          formattedOpponents.push({
              ...opponent,
              key: opponent.id, // إضافة مفتاح فريد لكل خصم
              type: 
                opponent.opponent_type === "مدعى عليه" 
                  ? "مدعى عليه" 
                  : opponent.opponent_type === "مدعي" 
                  ? "مدعي" 
                  : opponent.opponent_type === "individual" 
                  ? "فرد" 
                  : opponent.opponent_type === "company" 
                  ? "شركة" 
                  : "غير محدد",
              status: opponent.status || "نشط",
              // إضافة معلومات القضية
              case_name: caseInfo ? caseInfo.title : null,
              case_number: caseInfo ? caseInfo.case_number : null,
              case_id: caseInfo ? caseInfo.id : null,
              // حفظ معلومات القضية للاستخدامها لاحقاً
              caseInfo: caseInfo || null,
              // إضافة تنسيق للتواريخ إذا كانت موجودة
              created_at: opponent.created_at
                ? new Date(opponent.created_at).toISOString().split("T")[0]
                : null,
          });
        }

        setOpponents(formattedOpponents);
        if (formattedOpponents.length === 0) {
          message.info("لا يوجد خصوم في النظام حالياً");
        }
      } else {
        throw new Error("تنسيق البيانات غير صحيح");
      }
    } catch (err) {
      logger.error("Error fetching opponents:", err);
      setError(
        "حدث خطأ في جلب بيانات الخصوم: " +
          (err.response?.data?.message || err.message)
      );
      message.error("فشل في جلب بيانات الخصوم");
    } finally {
      setLoading(false);
    }
  };

  const fetchCases = async () => {
    try {
      const response = await axiosInstance.get("/cases?limit=100");

      if (response.data && response.data.data) {
        setCases(response.data.data || []);
      } else {
        throw new Error("تنسيق البيانات غير صحيح");
      }
    } catch (error) {
      logger.error("Error fetching cases:", error);
      setError(
        "حدث خطأ في جلب بيانات القضايا: " +
          (error.response?.data?.message || error.message)
      );
      message.error("فشل في جلب بيانات القضايا");
    }
  };

  const fetchStats = async () => {
    try {
      // جلب بيانات الخصوم مباشرة بدلاً من الإحصائيات
      const opponentsResponse = await axiosInstance.get(
        `/opponents?include_inactive=${showInactive}`
      );

      if (opponentsResponse.data && opponentsResponse.data.data) {
        const opponentsData = opponentsResponse.data.data;

        // حساب عدد المدعين والمدعى عليهم من بيانات الخصوم
        let plaintiffs = 0;  // مدعون
        let defendants = 0;  // مدعى عليهم
        let with_lawyers = 0;
        let with_phones = 0;
        let with_emails = 0;

        opponentsData.forEach(opponent => {
          if (opponent.opponent_type === "مدعي") {
            plaintiffs++;
          } else if (opponent.opponent_type === "مدعى عليه") {
            defendants++;
          }

          if (opponent.lawyer_name) with_lawyers++;
          if (opponent.phone) with_phones++;
          if (opponent.email) with_emails++;
        });

        const statsData = {
          total_opponents: opponentsData.length,
          plaintiffs: plaintiffs,
          defendants: defendants,
          with_lawyers: with_lawyers,
          with_phones: with_phones,
          with_emails: with_emails,
        };

        setStats(statsData);
      } else {
        logger.error("Invalid opponents response format:", opponentsResponse.data);
        throw new Error("تنسيق البيانات غير صحيح");
      }
    } catch (error) {
      logger.error("Error fetching stats:", error);
      setError(
        "حدث خطأ في جلب بيانات الإحصائيات: " +
          (error.response?.data?.message || error.message)
      );
      message.error("فشل في جلب بيانات الإحصائيات");
    }
  };

  const handleCaseChange = (value) => {
    setSelectedCaseId(value);
  };

  const handleOpponentAdded = (newOpponent) => {
    fetchOpponents();
    fetchStats();
  };

  const handleOpponentUpdated = (updatedOpponent) => {
    fetchOpponents();
    fetchStats();
  };

  const getCaseName = (opponent) => {
    return opponent.case_number && opponent.case_name 
      ? `${opponent.case_number} - ${opponent.case_name}` 
      : "غير محدد";
  };

  // Modified handlers to work with API
  const handleDeleteOpponent = async (id) => {
    try {
      const opponentToDelete = opponents.find((o) => o.id === id);

      // SweetAlert2 confirmation dialog
      const result = await Swal.fire({
        title: "تأكيد الحذف",
        html: `
                هل أنت متأكد من حذف الخصم التالي؟<br>
                <b>الاسم:</b> ${opponentToDelete.name}<br>
                <b>رقم الهاتف:</b> ${opponentToDelete.phone || "غير محدد"}<br>
                <b>القضية:</b> ${
                  getCaseName(opponentToDelete) || "غير محدد"
                }
            `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "نعم، حذف",
        cancelButtonText: "إلغاء",
        reverseButtons: true,
      });

      if (!result.isConfirmed) {
        return; // تم إلغاء الحذف
      }

      // إظهار مؤشر التحميل
      Swal.fire({
        title: "جاري الحذف...",
        text: "يرجى الانتظار",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await axiosInstance.delete(`/opponents/${id}`);

      if (response.status === 200) {
        setOpponents(opponents.filter((opponent) => opponent.id !== id));
        fetchStats(); // تحديث الإحصائيات بعد الحذف

        Swal.fire({
          title: "تم الحذف!",
          text: "تم حذف الخصم بنجاح",
          icon: "success",
          timer: 1500,
        });
      } else {
        throw new Error("فشل حذف الخصم");
      }
    } catch (error) {
      logger.error("Opponent deletion error:", error);

      Swal.fire({
        title: "خطأ!",
        text:
          error.response?.data?.message || error.message || "فشل في حذف الخصم",
        icon: "error",
      });
    }
  };

  const handleViewOpponent = (opponent) => {
    setSelectedOpponent(opponent);
    setShowViewModal(true);
  };

  const handleEditOpponent = (opponent) => {
    setSelectedOpponent(opponent);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setSelectedOpponent(null);
    fetchOpponents(); // إعادة تحميل قائمة الخصوم
  };

  // تحسين وظيفة البحث والفلترة والفرز
  const filteredOpponents = opponents
    .filter((opponent) => {
      // تحويل نص البحث إلى حروف صغيرة وإزالة المسافات الزائدة
      const search = searchTerm.toLowerCase().trim();

      // التأكد من وجود القيم قبل البحث فيها
      const nameMatch = opponent.name?.toLowerCase().includes(search);
      const phoneMatch = opponent.phone?.toLowerCase().includes(search);
      const emailMatch = opponent.email?.toLowerCase().includes(search);
      const lawyerMatch = opponent.lawyer_name?.toLowerCase().includes(search);

      // البحث في كل الحقول
      const matchesSearch =
        search === "" || // إذا كان حقل البحث فارغاً، اعرض كل النتائج
        nameMatch ||
        phoneMatch ||
        emailMatch ||
        lawyerMatch;

      // فلترة حسب نوع الخصم
      const matchesType =
        filterType === "الكل" || opponent.opponent_type === filterType;

      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      // تطبيق الفرز حسب الاختيار
      if (!sortBy) return 0;

      switch (sortBy) {
        case "newest":
          // ترتيب حسب الأحدث (تاريخ الإنشاء الأحدث)
          return new Date(b.created_at) - new Date(a.created_at);
        case "oldest":
          // ترتيب حسب الأقدم (تاريخ الإنشاء الأقدم)
          return new Date(a.created_at) - new Date(b.created_at);
        case "name-asc":
          // ترتيب حسب الاسم (من الألف إلى الياء)
          return a.name.localeCompare(b.name);
        case "name-desc":
          // ترتيب حسب الاسم (من الياء إلى الألف)
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });

  // حساب الـ pagination
  const totalPages = Math.ceil(filteredOpponents.length / itemsPerPage);
  const currentOpponents = filteredOpponents.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  // تحديث دالة تنسيق التاريخ
  const formatDate = (dateString) => {
    if (!dateString) return "لا يوجد";
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return "تاريخ غير صالح";
    }
  };

  // تعديل مكون جدول الخصوم
  const OpponentsTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 md:px-6 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              الخصم
            </th>
            <th className="px-2 md:px-6 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
              معلومات التواصل
            </th>
            <th className="px-2 md:px-6 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              معلومات المحامي
            </th>
            <th className="px-2 md:px-6 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
              الحالة
            </th>
            <th className="px-2 md:px-6 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              النوع
            </th>
            <th className="px-2 md:px-6 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
              القضية
            </th>
            <th className="px-2 md:px-6 py-2 md:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              إجراءات
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {currentOpponents.map((opponent) => (
            <tr
              key={opponent.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => handleViewOpponent(opponent)}
            >
              <td className="px-2 md:px-6 py-2 md:py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {opponent.name}
                </div>
                <div className="text-xs text-gray-500 md:hidden">
                  {opponent.phone ? (
                    <div className="flex items-center gap-2">
                      <a
                        href={`https://wa.me/${opponent.phone.replace(
                          /[^0-9]/g,
                          ""
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-800 flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                        {opponent.phone}
                      </a>
                    </div>
                  ) : (
                    "لا يوجد هاتف"
                  )}
                </div>
              </td>
              <td className="px-2 md:px-6 py-2 md:py-4 whitespace-nowrap hidden md:table-cell">
                <div className="text-sm text-gray-900">
                  {opponent.phone ? (
                    <div className="flex items-center gap-2">
                      <a
                        href={`https://wa.me/${opponent.phone.replace(
                          /[^0-9]/g,
                          ""
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-800 flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                        {opponent.phone}
                      </a>
                    </div>
                  ) : (
                    "لا يوجد هاتف"
                  )}
                </div>
                {opponent.email && (
                  <div className="text-xs text-gray-500 truncate max-w-[200px]">
                    {opponent.email}
                  </div>
                )}
              </td>
              <td className="px-2 md:px-6 py-2 md:py-4 whitespace-nowrap hidden md:table-cell">
                {opponent.lawyer_name ? (
                  <div>
                    <div className="text-sm text-gray-900">
                      {opponent.lawyer_name}
                    </div>
                    {opponent.lawyer_phone && (
                      <div className="text-xs text-gray-500 flex items-center gap-2">
                        <a
                          href={`https://wa.me/${opponent.lawyer_phone.replace(
                            /[^0-9]/g,
                            ""
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-800 flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                          </svg>
                          {opponent.lawyer_phone}
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400">لا يوجد</span>
                )}
              </td>
              <td className="px-2 md:px-6 py-2 md:py-4 whitespace-nowrap">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    opponent.status === "active"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {opponent.status === "active" ? "نشط" : "غير نشط"}
                </span>
              </td>
              <td className="px-2 md:px-6 py-2 md:py-4 whitespace-nowrap">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    opponent.opponent_type === "مدعى عليه"
                      ? "bg-red-100 text-red-800"
                      : opponent.opponent_type === "مدعي"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {opponent.opponent_type === "مدعى عليه" ? "مدعى عليه" : opponent.opponent_type === "مدعي" ? "مدعي" : "غير محدد"}
                </span>
              </td>
              <td className="px-2 md:px-6 py-2 md:py-4 whitespace-nowrap hidden sm:table-cell">
                <div className="text-sm text-gray-900">
                  {opponent.case_id ? (
                    <div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `/cases/${opponent.case_id}`;
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {opponent.case_number} - {opponent.case_name}
                      </button>
                      <div className="text-xs text-gray-500 mt-1">
                        رقم القضية: {opponent.case_number}
                      </div>
                    </div>
                  ) : (
                    "غير محدد"
                  )}
                </div>
              </td>
              <td className="px-2 md:px-6 py-2 md:py-4 whitespace-nowrap text-center text-sm font-medium">
                <div
                  className="flex justify-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="text-blue-600 hover:text-blue-900 ml-2"
                    onClick={() => handleViewOpponent(opponent)}
                  >
                    عرض
                  </button>
                  <button
                    type="button"
                    className="text-green-600 hover:text-green-900 ml-2"
                    onClick={() => handleEditOpponent(opponent)}
                  >
                    تعديل
                  </button>
                  <button
                    type="button"
                    className="text-red-600 hover:text-red-900"
                    onClick={() => handleDeleteOpponent(opponent.id)}
                  >
                    حذف
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading && opponents.length === 0) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <Spin size="large" tip="جاري تحميل الخصوم..." />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold">خطأ!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">الخصوم</h1>
          <p className="text-gray-600">إدارة خصوم القضايا</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  إجمالي الخصوم
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {stats.total_opponents}
                </dd>
              </dl>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  مدعون
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {stats.plaintiffs}
                </dd>
              </dl>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  مدعى عليهم
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {stats.defendants}
                </dd>
              </dl>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  لديهم محامين
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {stats.with_lawyers}
                </dd>
              </dl>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  لديهم هواتف
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {stats.with_phones}
                </dd>
              </dl>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  لديهم بريد إلكتروني
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {stats.with_emails}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                <div className="relative rounded-md shadow-sm w-full sm:w-64">
                  <input
                    type="text"
                    name="search"
                    id="search"
                    className="block w-full pr-10 py-2 text-right border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="البحث عن خصم..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="text-sm text-gray-500">.</span>
                  <select
                    id="filter-combined"
                    name="filter-combined"
                    className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    value={
                      filterType === "الكل" && !showInactive
                        ? "all-active"
                        : filterType === "الكل" && showInactive
                        ? "all"
                        : filterType === "مدعى عليه" && !showInactive
                        ? "مدعى عليه-active"
                        : filterType === "مدعى عليه" && showInactive
                        ? "مدعى عليه"
                        : filterType === "مدعي" && !showInactive
                        ? "مدعي-active"
                        : filterType === "مدعي" && showInactive
                        ? "مدعي"
                        : "all"
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "all-active") {
                        setFilterType("الكل");
                        setShowInactive(false);
                      } else if (value === "all") {
                        setFilterType("الكل");
                        setShowInactive(true);
                      } else if (value === "مدعى عليه-active") {
                        setFilterType("مدعى عليه");
                        setShowInactive(false);
                      } else if (value === "مدعى عليه") {
                        setFilterType("مدعى عليه");
                        setShowInactive(true);
                      } else if (value === "مدعي-active") {
                        setFilterType("مدعي");
                        setShowInactive(false);
                      } else if (value === "مدعي") {
                        setFilterType("مدعي");
                        setShowInactive(true);
                      }
                    }}
                  >
                    <option value="all">الكل</option>
                    <option value="all-active">الخصوم النشطون</option>
                    <option value="مدعى عليه">جميع المدعى عليهم</option>
                    <option value="مدعى عليه-active">المدعى عليهم النشطون</option>
                    <option value="مدعي">جميع المدعين</option>
                    <option value="مدعي-active">المدعين النشطون</option>
                  </select>
                </div>
                <select
                  id="sort-by"
                  name="sort-by"
                  className="block w-full sm:w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="">الترتيب حسب</option>
                  <option value="newest">الأحدث</option>
                  <option value="oldest">الأقدم</option>
                  <option value="name-asc">الاسم (أ-ي)</option>
                  <option value="name-desc">الاسم (ي-أ)</option>
                </select>
              </div>
              <div>
                <Button
                  type="primary"
                  onClick={() => setShowAddModal(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  إضافة خصم جديد
                </Button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {filteredOpponents.length > 0 ? (
              <OpponentsTable />
            ) : (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  لا توجد خصوم
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || filterType !== "الكل" || sortBy
                    ? "لم يتم العثور على خصوم تطابق معايير البحث"
                    : "ابدأ بإضافة خصم جديد"}
                </p>
                <div className="mt-6">
                  <Button
                    type="primary"
                    onClick={() => setShowAddModal(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    إضافة خصم جديد
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredOpponents.length > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  السابق
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  التالي
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    عرض{" "}
                    <span className="font-medium">
                      {(currentPage - 1) * itemsPerPage + 1}
                    </span>
                    {" "}- {" "}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, filteredOpponents.length)}
                    </span>
                    {" "}من{" "}
                    <span className="font-medium">{filteredOpponents.length}</span>
                    {" "}خصم
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">السابق</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {getPageNumbers().map((number) => (
                      <button
                        key={number}
                        onClick={() => handlePageChange(number)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === number
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {number}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">التالي</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Add Opponent Modal */}
        {showAddModal && (
          <AddOpponentModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            caseId={""}
            caseNumber={""}
            onOpponentAdded={handleOpponentAdded}
          />
        )}

        {/* View Opponent Modal */}
        {showViewModal && selectedOpponent && (
          <ViewOpponentModal
            isOpen={showViewModal}
            onClose={() => setShowViewModal(false)}
            opponentId={selectedOpponent.id}
            onOpponentUpdated={handleOpponentUpdated}
          />
        )}

        {/* Edit Opponent Modal */}
        {showEditModal && selectedOpponent && (
          <EditOpponentModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            opponentId={selectedOpponent.id}
            onOpponentUpdated={handleOpponentUpdated}
          />
        )}
      </div>
    </Layout>
  );
};

export default OpponentsPage;
